import { api } from "./api.ts";

import type { AdjusterType, PaginatedResponse } from "types/DataTypes";

type GetAdjustersArgs = {
  client_id?: string;
  searchTerm?: string;
  limit?: number;
  offset?: number;
};

type GetAdjusterByIdArgs = {
  id: number;
};

type CreateAdjusterArgs = {
  data: AdjusterType;
};

type PatchAdjusterArgs = {
  id: string | number;
  data: Partial<AdjusterType>;
};

type DeleteAdjusterArgs = {
  id: number;
};

export const adjusterAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getAdjusters: build.query<
      PaginatedResponse<AdjusterType>,
      GetAdjustersArgs
    >({
      query: ({ client_id, searchTerm, limit, offset }) => {
        const params = new URLSearchParams();
        if (client_id) params.append("client_id", client_id);
        // DRF SearchFilter reads the term from `filter` (SEARCH_PARAM in
        // settings), not the default `search`; matches employer-api
        if (searchTerm) params.append("filter", searchTerm);
        if (limit !== undefined) params.append("limit", String(limit));
        if (offset !== undefined) params.append("offset", String(offset));

        return {
          url: `api/v1/adjusters/?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Adjusters"],
    }),
    getAdjusterById: build.query<AdjusterType, GetAdjusterByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/adjusters/${id}/`,
        method: "GET",
      }),
      providesTags: ["Adjuster"],
    }),
    createAdjuster: build.mutation<AdjusterType, CreateAdjusterArgs>({
      query: ({ data }) => {
        return {
          url: `api/v1/adjusters/`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["Adjuster", "Adjusters"],
    }),
    patchAdjusterById: build.mutation<AdjusterType, PatchAdjusterArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/adjusters/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Adjuster", "Adjusters"],
    }),
    deleteAdjusterById: build.mutation<void, DeleteAdjusterArgs>({
      query: ({ id }) => ({
        url: `api/v1/adjusters/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Adjusters"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdjustersQuery,
  useGetAdjusterByIdQuery,
  useCreateAdjusterMutation,
  usePatchAdjusterByIdMutation,
  useDeleteAdjusterByIdMutation,
} = adjusterAPI;
